/**
 * ExportEmployeesModal — eksport PRACOWNIKÓW MY → Symfonia FK (kierunek odwrotny do
 * SyncEmployeesModal, który pobiera FK → my). Odbicie ExportContractorsModal.
 *
 * Modal dwuetapowy:
 *  1. **Potwierdzenie** — krótkie wyjaśnienie + ostrzeżenie o scalaniu (nie kasujemy danych
 *     w Symfonii, których u nas brak).
 *  2. **Progress** — po wysłaniu jobu polling `GET /erp-sync-jobs/:id` + wspólny widok wyniku
 *     `SyncResultPanel` (pigułki klikalne + tabela rekordów z drill-downem „co się zmieniło").
 *
 * Endpoint `POST /erp-integrations/:id/push-employees` — tylko FK w trybie bezpośrednim.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

import { isJobInFlight } from './job-in-flight';
import { type SyncRecordDetail, SyncResultPanel, type SyncResultTile } from './SyncResultPanel';
import type { JobDto } from './TestConnectionModal';

type Stage = 'confirm' | 'progress';

interface Props {
  open: boolean;
  integrationId: string | null;
  integrationName: string;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 3 * 60_000;

interface ExportStats {
  added?: number;
  updated?: number;
  unchanged?: number;
  skipped?: number;
  excluded?: number;
}

interface ExportResultPayload {
  outcome?: 'ok' | 'partial' | 'no_op';
  stats?: ExportStats;
  warnings?: string[];
  /** Listy rekordów per kubełek (drill-down w pigułkach). */
  details?: Record<string, SyncRecordDetail[]>;
}

const PREFIX = 'tenant.settings.integrations.exportEmployees';

export function ExportEmployeesModal({ open, integrationId, integrationName, onClose }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<Stage>('confirm');
  const [expanded, setExpanded] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!open) {
      setStage('confirm');
      setExpanded(false);
      setJobId(null);
      setTimedOut(false);
      return;
    }
    if (stage !== 'progress' || !jobId) return;
    const timer = window.setTimeout(() => setTimedOut(true), POLL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [open, stage, jobId]);

  const startExportMutation = useMutation({
    mutationFn: async () => {
      if (!integrationId) throw new Error('No integrationId');
      const res = await api.post<{ jobId: string }>(
        `/erp-integrations/${integrationId}/push-employees`,
        {},
      );
      return res.data.jobId;
    },
    onSuccess: (id) => {
      setJobId(id);
      setStage('progress');
    },
  });

  const jobQ = useQuery({
    queryKey: ['erp-sync-job', jobId],
    queryFn: async () => {
      const res = await api.get<{ job: JobDto }>(`/erp-sync-jobs/${jobId}`);
      return res.data.job;
    },
    enabled: !!jobId && open && !timedOut && stage === 'progress',
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return POLL_INTERVAL_MS;
    },
  });

  useEffect(() => {
    if (jobQ.data?.status === 'completed' || jobQ.data?.status === 'failed') {
      void queryClient.invalidateQueries({ queryKey: ['erp-integrations'] });
    }
  }, [jobQ.data?.status, queryClient]);

  if (!open) return null;

  const job = jobQ.data ?? null;
  const result = (job?.result ?? null) as ExportResultPayload | null;

  const d = result?.details ?? {};
  const s = result?.stats ?? {};
  const resultTiles: SyncResultTile[] = [
    {
      key: 'added',
      label: t(`${PREFIX}.stats.added`),
      count: s.added ?? 0,
      tone: 'added',
      records: d.added ?? [],
    },
    {
      key: 'updated',
      label: t(`${PREFIX}.stats.updated`),
      count: s.updated ?? 0,
      tone: 'updated',
      records: d.updated ?? [],
    },
    {
      key: 'unchanged',
      label: t(`${PREFIX}.stats.unchanged`),
      count: s.unchanged ?? 0,
      tone: 'muted',
      records: d.unchanged ?? [],
    },
    {
      key: 'skipped',
      label: t(`${PREFIX}.stats.skipped`),
      count: s.skipped ?? 0,
      tone: 'warning',
      records: d.skipped ?? [],
    },
    {
      key: 'excluded',
      label: t(`${PREFIX}.stats.excluded`),
      count: s.excluded ?? 0,
      tone: 'muted',
      records: d.excluded ?? [],
    },
  ];

  // Wysyłanie żądania albo eksport w toku — tło i Esc nie zamykają okna (X i „Zamknij" działają).
  const running =
    startExportMutation.isPending ||
    (stage === 'progress' && isJobInFlight(job, timedOut, jobQ.isError));

  return (
    <Modal
      open
      onClose={onClose}
      title={t(`${PREFIX}.title`, { name: integrationName })}
      size="lg"
      expandable
      expanded={expanded}
      onExpandedChange={setExpanded}
      testId="export-employees-modal"
      closeOnBackdrop={!running}
      closeOnEsc={!running}
      footer={
        <>
          {stage === 'confirm' && (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => startExportMutation.mutate()}
                disabled={startExportMutation.isPending}
                data-testid="export-employees-start-btn"
              >
                {startExportMutation.isPending
                  ? t(`${PREFIX}.starting`)
                  : t(`${PREFIX}.startButton`)}
              </Button>
            </>
          )}
          {stage === 'progress' && (
            <Button variant="primary" onClick={onClose} data-testid="export-employees-close-btn">
              {t('common.close')}
            </Button>
          )}
        </>
      }
    >
      {stage === 'confirm' && (
        <div className="space-y-4" data-testid="export-employees-stage-confirm">
          <p className="text-sm text-text-secondary">{t(`${PREFIX}.confirmHint`)}</p>
          <div className="rounded-md border border-accent-500/30 bg-accent-500/5 p-3 text-sm text-text-secondary">
            {t(`${PREFIX}.mergeNote`)}
          </div>
          {startExportMutation.isError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-400">
              {t(`${PREFIX}.startError`)}
            </div>
          )}
        </div>
      )}

      {stage === 'progress' && (
        <div
          className="flex h-full min-h-0 flex-col gap-4"
          data-testid={`export-employees-status-${
            job?.status === 'completed' || job?.status === 'failed'
              ? job.status
              : timedOut
                ? 'timeout'
                : (job?.status ?? 'running')
          }`}
        >
          {!timedOut && (!job || job.status === 'pending' || job.status === 'running') && (
            <div className="flex items-center gap-3 rounded-md bg-bg-secondary p-3">
              <Loader2 className="h-5 w-5 animate-spin text-accent-500" aria-hidden="true" />
              <div>
                <div className="font-medium text-text-primary">{t(`${PREFIX}.running`)}</div>
                <div className="text-xs text-text-tertiary">{t(`${PREFIX}.runningHint`)}</div>
              </div>
            </div>
          )}

          {timedOut && job?.status !== 'completed' && job?.status !== 'failed' && (
            <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
              <Clock className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" aria-hidden="true" />
              <div>
                <div className="font-medium text-red-600 dark:text-red-400">
                  {t(`${PREFIX}.timeoutTitle`)}
                </div>
                <div className="text-xs text-text-secondary">{t(`${PREFIX}.timeoutHint`)}</div>
              </div>
            </div>
          )}

          {job?.status === 'completed' && (
            <div className="flex min-h-0 flex-1 flex-col gap-3">
              <div className="flex shrink-0 items-start gap-3 rounded-md border border-green-500/30 bg-green-500/10 p-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" aria-hidden="true" />
                <div>
                  <div className="font-medium text-green-700 dark:text-green-400">
                    {t(`${PREFIX}.successTitle`)}
                  </div>
                  <div className="text-xs text-text-secondary">{t(`${PREFIX}.successHint`)}</div>
                </div>
              </div>

              <SyncResultPanel
                tiles={resultTiles}
                onNavigate={onClose}
                linkBase="/employees"
                testId="export-employees-result"
                fillHeight={expanded}
                listHeightClass="h-72"
              />
            </div>
          )}

          {job?.status === 'failed' && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
                <AlertCircle
                  className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400"
                  aria-hidden="true"
                />
                <div>
                  <div className="font-medium text-red-600 dark:text-red-400">
                    {t(`${PREFIX}.failedTitle`)}
                  </div>
                  <div className="text-xs text-text-secondary">{t(`${PREFIX}.failedHint`)}</div>
                </div>
              </div>
              {job.result && (
                <pre className="max-h-64 overflow-auto rounded-md border border-red-500/30 bg-red-500/5 p-3 font-mono text-xs text-red-700 dark:text-red-400">
                  {JSON.stringify(job.result, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
